import { useRef, useEffect, useState } from 'react';
import { blogPosts } from '../../../mocks/portfolio';

const BlogSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="blog" ref={sectionRef} className="section-padding bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-medium">Latest Insights</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2 text-gray-900 dark:text-white">
              技术
              <span className="font-serif italic text-gradient ml-2">博客</span>
            </h2>
            <p className="text-sm text-gray-400 mt-3">思考 AI 产品与人类体验之间的张力</p>
          </div>
          <a
            href="#blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-orange-500 hover:gap-3 transition-all cursor-pointer whitespace-nowrap self-start md:self-auto"
            rel="nofollow"
          >
            查看全部文章
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Blog grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.map((post, i) => (
            <article
              key={post.id}
              data-animate
              className="opacity-0 translate-y-8 transition-all duration-700"
              style={{ transitionDelay: `${i * 100}ms` }}
              onMouseEnter={() => setHovered(post.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className={`group flex flex-col h-full bg-stone-50 dark:bg-gray-800 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 cursor-pointer transition-all duration-300 ${hovered === post.id ? '-translate-y-2 shadow-xl shadow-gray-100 dark:shadow-black/30' : ''}`}>
                {/* Cover */}
                <div className="relative h-44 overflow-hidden flex-shrink-0">
                  <img
                    src={post.image}
                    alt={`${post.title} - 技术博客`}
                    title={post.title}
                    className={`w-full h-full object-cover object-top transition-transform duration-500 ${hovered === post.id ? 'scale-105' : 'scale-100'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-medium rounded-full bg-orange-500 text-white">
                    {post.category}
                  </span>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-xs text-gray-400 leading-relaxed flex-1 line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line text-orange-400" />
                      <span>{post.readTime}</span>
                    </div>
                    <span>{post.date}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-500 mt-16">
          <div className="bg-gradient-to-r from-orange-50 to-rose-50 dark:from-orange-900/20 dark:to-rose-900/10 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 id="newsletter" className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                <a href="#newsletter">订阅博客更新</a>
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">每月精选 AI 产品思考，直达你的邮箱</p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 flex-shrink-0">
                <i className="ri-mail-send-line text-orange-500" />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">关注我的社交媒体获取更新</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
